import type { TFunction } from 'i18next';

export interface GroceryBannerCopyArgs {
  uncheckedCount: number;
  recipeMealCount: number;
  isOtherWeek: boolean;
  allCovered: boolean;
}

export interface GroceryBannerCopy {
  label: string;
  /**
   * Second line for the surfaces that have room for one. Null when `label`
   * already carries the whole sentence, which is the case for the buy state.
   */
  sublabel: string | null;
}

/**
 * The one place the grocery counter's words are decided.
 *
 * Every surface renders `label`, so the count can never again arrive without
 * saying what it is *for*. It used to: only the full-size WeekCounterBanner
 * carried the "pour vos N repas" subtitle, and WeeklyMobile only renders that
 * banner when there is no next meal. A user with a freshly generated week
 * always has a next meal, so the compact WeekSummaryLine was all they ever
 * saw, and "62 articles à acheter" read as a chore rather than the cost of a
 * plan (#623).
 *
 * Lives in its own module, free of expo-router and redux, because jest runs in
 * `node` here with `react-native` stubbed and no render harness installed —
 * copy is only testable if it sits outside the hook.
 *
 * The buy state deliberately has no sublabel. The line it replaces claimed
 * "le reste, vous l'avez", which is false on an empty pantry: nothing was
 * subtracted but the fixed staples. That is #625, and widening its reach to
 * every surface would have spread the bug instead of the context.
 */
export function buildGroceryBannerCopy(
  t: TFunction,
  { uncheckedCount, recipeMealCount, isOtherWeek, allCovered }: GroceryBannerCopyArgs
): GroceryBannerCopy {
  if (isOtherWeek) {
    return {
      label: t('weekly.shopThisWeek', "Faire l'épicerie de cette semaine"),
      sublabel: t('weekly.shopThisWeekSub', {
        defaultValue: '{{count}} repas planifiés',
        count: recipeMealCount,
      }),
    };
  }

  if (allCovered) {
    return {
      label: t('weekly.counterAllCovered', 'Tout est au garde-manger'),
      sublabel: t('weekly.counterSubAllCovered', {
        defaultValue: 'pour vos {{count}} repas · rien à acheter',
        count: recipeMealCount,
      }),
    };
  }

  return {
    // `count` drives i18next pluralisation and here it is the *meal* count, so
    // English gets "1 meal" / "7 meals". The item count rides along as `items`
    // because a key can only pluralise on one number.
    label: t('weekly.counterItemsToBuyForMeals', {
      defaultValue: '{{items}} à acheter pour {{count}} repas',
      items: uncheckedCount,
      count: recipeMealCount,
    }),
    sublabel: null,
  };
}
